import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import {
    trackL1ModuleClick,
    trackL2ModuleClick,
    trackCreatorModuleClick,
    trackRoomModuleClick,
} from '../../utils/tracking/searchBrowse/nullResultsTracking';

// components
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle';
import { SbRespNullSearchResultsHeader } from './SbRespNullSearchResultsHeader';
import { CategoryTiles, CategoryRooms } from 'dibs-buyer-layout/exports/CategoryLinks';
import { SbRespFollowSearchBanner } from '../SbRespFollowSearchBanner/SbRespFollowSearchBanner';
import { SbSharedFollowSearchProvider } from '../SbSharedFollowSearchProvider/SbSharedFollowSearchProvider';

import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
const { SEARCH } = pageTypes;

const CREATOR_CATEGORY_TYPE = 'tile';

const onL1ModuleClick = (e, { vertical }) => {
    const l1Name = vertical.category;

    if (vertical.type === CREATOR_CATEGORY_TYPE) {
        return trackCreatorModuleClick(l1Name);
    }
    return trackL1ModuleClick(l1Name);
};

const onL2ModuleClick = (e, { vertical, index }) =>
    trackL2ModuleClick(vertical.category, index === 0);

const onRoomModuleClick = (e, { room }) => trackRoomModuleClick(room.type);

export const SbRespNullSearchResultsComponent = props => {
    const { user, page, viewer, searchTerm, searchBrowse, uriRef } = props;
    const { isShopByRoomEnabled, isSbRespFollowSearchBannerEnabled } = viewer;
    const { pageType } = searchBrowse;
    const isSearch = pageType === SEARCH;

    return (
        <SbSharedFollowSearchProvider
            user={user}
            page={page}
            uriRef={uriRef}
            searchTerm={searchTerm}
            searchBrowse={searchBrowse}
            thinTrackingEnabled={false}
        >
            {({ showFollowButton, isFollowing, followSearchValue, onFollowSearchCompleted }) => (
                <React.Fragment>
                    {isSearch ? (
                        <SbRespNullSearchResultsHeader
                            searchTerm={searchTerm}
                            searchBrowse={searchBrowse}
                        />
                    ) : (
                        <SbSharedTitle searchBrowse={searchBrowse} searchTerm={searchTerm} />
                    )}
                    {isSbRespFollowSearchBannerEnabled && (
                        <SbRespFollowSearchBanner
                            user={user}
                            viewer={viewer}
                            buttonSize="large"
                            uriRef={uriRef}
                            isFollowing={isFollowing}
                            showFollowButton={showFollowButton}
                            followSearchValue={followSearchValue}
                            onFollowSearchCompleted={onFollowSearchCompleted}
                        />
                    )}
                    <CategoryTiles
                        showModules
                        viewer={viewer}
                        onMainTileClick={onL1ModuleClick}
                        onModuleTileClick={onL2ModuleClick}
                    />
                    {isShopByRoomEnabled && <CategoryRooms onClick={onRoomModuleClick} />}
                </React.Fragment>
            )}
        </SbSharedFollowSearchProvider>
    );
};

SbRespNullSearchResultsComponent.propTypes = {
    user: PropTypes.object,
    uriRef: PropTypes.string,
    viewer: PropTypes.object,
    searchTerm: PropTypes.string,
    page: PropTypes.number.isRequired,
    searchBrowse: PropTypes.object.isRequired,
};

export const SbRespNullSearchResults = createFragmentContainer(SbRespNullSearchResultsComponent, {
    user: graphql`
        fragment SbRespNullSearchResults_user on User {
            ...SbRespFollowSearchBanner_user
            ...SbSharedFollowSearchProvider_user
        }
    `,
    viewer: graphql`
        fragment SbRespNullSearchResults_viewer on Viewer {
            isShopByRoomEnabled: isEnabledByLocale(featureName: "SHOP_BY_ROOM")
            isSbRespFollowSearchBannerEnabled: isEnabledByLocale(
                featureName: "NON_MVP_SEARCH_BROWSE"
            )
            ...CategoryTiles_viewer
            ...SbRespFollowSearchBanner_viewer
        }
    `,
    searchBrowse: graphql`
        fragment SbRespNullSearchResults_searchBrowse on SearchBrowseQueryConnection {
            pageType
            ...SbSharedFollowSearchProvider_searchBrowse
            ...SbRespNullSearchResultsHeader_searchBrowse
            ...SbSharedTitle_searchBrowse
        }
    `,
});
